import { useContext, useState } from 'react';
import { RouteContext } from 'pages/CompanyContainer';
import { InboxRecipient, inboxThreadUrl } from 'pages/Inbox/inboxUtils';
import { ICompany } from 'types/company';

/** Who a message goes out as. A community counts as a company here — the API keys both by company id. */
export type SendAs = { type: 'user' } | { type: 'company'; company: ICompany };

export interface SendAsChoice {
  selected: SendAs;
  /** Two or more is what makes a picker worth showing. Omitted means the identity is fixed. */
  options?: SendAs[];
  onChange?: (next: SendAs) => void;
}

export const asUser: SendAs = { type: 'user' };

export function sendAsKey(sendAs: SendAs) {
  return sendAs.type === 'company' ? `company-${sendAs.company.id}` : 'user';
}

export function sendAsCompany(sendAs?: SendAs) {
  return sendAs?.type === 'company' ? sendAs.company : undefined;
}

/**
 * Resolves who a new conversation should go out as. Inside a community or company you administer the
 * default is that entity, because acting on its behalf is the common case there; everywhere else it
 * is you.
 *
 * The context comes from `RouteContext` rather than the route params on purpose: community routes are
 * spelled both `:communitySlug` and `:communityslug` across the app, and the containers already hold
 * the entity and its scopes. They also gate their page body on those resources, so by the time a
 * contact button exists there is nothing left to wait for.
 */
export default function useSendAs(opts?: {
  /** Keeps us from offering to write to an entity as itself. */
  excludeId?: number;
  /** Set false where writing as yourself is refused anyway, so the picker can't strand you. */
  allowUser?: boolean;
}): SendAsChoice {
  const { entity, canActAsEntity } = useContext(RouteContext);
  // Held as a key rather than the object: the options are rebuilt every render, and a choice that
  // stops being available should quietly fall back to the default rather than linger.
  const [overrideKey, setOverrideKey] = useState<string>();

  const contextCompany = canActAsEntity && entity && entity.id !== opts?.excludeId ? entity : undefined;
  // Context first, so options[0] is the default.
  const options: SendAs[] = [
    ...(contextCompany ? [{ type: 'company' as const, company: contextCompany }] : []),
    ...(opts?.allowUser === false ? [] : [asUser]),
  ];

  return {
    // Never empty: with nothing else on offer the caller still has to render something, and its own
    // guard is what refuses the message.
    selected: options.find(option => sendAsKey(option) === overrideKey) ?? options[0] ?? asUser,
    options,
    onChange: (next: SendAs) => setOverrideKey(sendAsKey(next)),
  };
}

/**
 * Where a conversation with this recipient would live if you held it as the company or community
 * whose part of the app you are in — its inbox. Undefined when there is no such entity, or when you
 * may not act for it, in which case the conversation is yours and belongs in a dialog.
 */
export function useEntityInboxUrl(recipient?: InboxRecipient): string | undefined {
  const { value, entity, canActAsEntity } = useContext(RouteContext);

  if (!recipient || !entity || !canActAsEntity) {
    return undefined;
  }

  return inboxThreadUrl(value, entity.slug, recipient);
}
